import {
  Badge,
  BlockStack,
  Button,
  Card,
  InlineGrid,
  InlineStack,
  Spinner,
  Text,
  Banner,
  Link as PolarisLink,
} from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, ConflictError, PaywallError } from '../lib/api.js';
import type {
  ActivityItem,
  BillingStatus,
  ComplianceIssue,
  FindingsPage,
  FixedProduct,
  ScanJob,
  ScanResult,
  Severity,
} from '../lib/types.js';
import { AppPage } from '../components/Layout.js';
import { ScoreRing } from '../components/ScoreRing.js';
import { SeverityBadge } from '../components/SeverityBadge.js';
import { MetricCard } from '../components/MetricCard.js';
import { groupFindingsByProduct } from '../lib/group-findings.js';
import {
  contentTypeLabel,
  formatPublicRegulations,
  formatRegulationsShort,
  regulationLabel,
  riskBandKey,
} from '../lib/regulation-labels.js';

/**
 * Dashboard state machine:
 *   loading    → initial fetch
 *   pre        → first-time / no scan yet (design #01)
 *   scanning   → polling progress (design #02)
 *   complete   → scan just finished (design #03)
 *   results    → results screen (design #04 free / #05 paid)
 */
type View = 'loading' | 'pre' | 'scanning' | 'complete' | 'results';

const POLL_INTERVAL_MS = 2_000;
const EU_DEADLINE = new Date('2026-08-02T00:00:00Z');

function daysUntilDeadline(): number {
  const diff = EU_DEADLINE.getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / 86_400_000));
}

/**
 * Extract the numeric portion of a Shopify GID (`gid://shopify/Product/123`).
 * Used as a recency fallback when scan rows pre-date the createdAt field —
 * Shopify allocates IDs monotonically per shop, so larger ID ≈ newer product.
 */
function gidNumeric(gid: string): number {
  const m = /\/(\d+)(?:\?|$)/.exec(gid);
  return m ? Number(m[1]) : 0;
}

export function Dashboard(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [view, setView] = useState<View>('loading');
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [scan, setScan] = useState<ScanResult | null>(null);
  // Previous scan's score + date — drives the score-trend card. `null` when
  // this is the shop's first scan.
  const [previous, setPrevious] = useState<{ score: number; createdAt: string } | null>(null);
  // Count of products that appear in the latest scan but not the prior one —
  // drives the "New products since last scan" card on monitoring dashboards.
  const [newProductCount, setNewProductCount] = useState(0);
  const [job, setJob] = useState<ScanJob | null>(null);
  const [findings, setFindings] = useState<ComplianceIssue[]>([]);
  // Products with a live disclosure — populated from /fixed-products, which
  // joins product_description_backups with the current scan. Durable across
  // rescans so "88 → 22 Fixed" rows survive a re-scan that drops the issue.
  const [fixedProducts, setFixedProducts] = useState<FixedProduct[]>([]);
  // Recent Activity feed — flat time-ordered rows that the UI groups by day
  // + collapses when ≥3 same-kind events hit in one day. Only rendered for
  // monitoring-tier shops where the Recent Activity card appears.
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [startBusy, setStartBusy] = useState(false);
  const [scanStartTime, setScanStartTime] = useState(0);
  const [totalProducts, setTotalProducts] = useState<number | null>(null);

  // Pull all dashboard data. `silent` keeps `view` stable so the page doesn't
  // flicker back to a spinner when we're just refreshing in the background
  // (e.g. tab regained focus, or polling after a fix landed).
  const loadDashboard = useCallback(async (signal?: AbortSignal, silent = false) => {
    try {
      const [b, s, pc] = await Promise.all([
        api.get<BillingStatus>('/api/billing/status', signal),
        api.get<{ scan: ScanResult | null; previous: { score: number; createdAt: string } | null; newProductCount: number }>('/api/scan/latest', signal),
        api.get<{ count: number | null }>('/api/shop/product-count', signal),
      ]);
      setBilling(b);
      setScan(s.scan);
      setPrevious(s.previous);
      setNewProductCount(s.newProductCount);
      if (pc.count !== null) setTotalProducts(pc.count);
      if (!silent) setView(s.scan ? 'results' : 'pre');
      if (s.scan) {
        const [f, fp, act] = await Promise.all([
          api.get<FindingsPage>(`/api/scan/${s.scan.id}/findings?limit=100`, signal),
          api.get<{ items: FixedProduct[] }>(`/api/scan/${s.scan.id}/fixed-products`, signal),
          api.get<{ items: ActivityItem[] }>('/api/shop/activity?limit=50', signal),
        ]);
        setFindings(f.items);
        setFixedProducts(fp.items);
        setActivity(act.items);
      }
    } catch (e) {
      if ((e as Error).name === 'AbortError') return;
      if (!silent) {
        setError((e as Error).message);
        setView('pre');
      }
    }
  }, []);

  // Initial load: billing status + last scan.
  useEffect(() => {
    const ac = new AbortController();
    void loadDashboard(ac.signal);
    return () => ac.abort();
  }, [loadDashboard]);

  // Refetch when tab regains focus — covers the user who alt-tabs back to
  // Shopify after editing a product, or returns from a long-running fix.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void loadDashboard(undefined, true);
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [loadDashboard]);

  // Just navigated back from BulkFix / ProductDetail with `justFixed`?
  // The fix worker is async — the row may still be in flight when we land.
  // Poll every 1.5s for up to 12s, stopping early once fixedProducts grows
  // or score drops (i.e. worker finished writing).
  useEffect(() => {
    const state = location.state as { justFixed?: boolean } | null;
    if (!state?.justFixed) return;
    // Clear the flag so a manual refresh of the dashboard doesn't re-poll.
    navigate(location.pathname, { replace: true, state: {} });
    let cancelled = false;
    let elapsed = 0;
    const id = setInterval(() => {
      if (cancelled) return;
      elapsed += 1500;
      void loadDashboard(undefined, true);
      if (elapsed >= 12_000) clearInterval(id);
    }, 1500);
    void loadDashboard(undefined, true);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [location.state, location.pathname, navigate, loadDashboard]);

  // Poll when a job is running.
  const jobRef = useRef<ScanJob | null>(null);
  jobRef.current = job;
  useEffect(() => {
    if (view !== 'scanning' || !job) return;
    const id = setInterval(async () => {
      const current = jobRef.current;
      if (!current) return;
      try {
        const next = await api.get<ScanJob>(`/api/scan/progress/${current.jobId}`);
        setJob(next);
        if (next.status === 'completed' && next.scanResultId) {
          const s = await api.get<{ scan: ScanResult | null; previous: { score: number; createdAt: string } | null; newProductCount: number }>('/api/scan/latest');
          setScan(s.scan);
          setPrevious(s.previous);
          setNewProductCount(s.newProductCount);
          if (s.scan) {
            const [f, fp, act] = await Promise.all([
              api.get<FindingsPage>(`/api/scan/${s.scan.id}/findings?limit=100`),
              api.get<{ items: FixedProduct[] }>(`/api/scan/${s.scan.id}/fixed-products`),
              api.get<{ items: ActivityItem[] }>('/api/shop/activity?limit=50'),
            ]);
            setFindings(f.items);
            setFixedProducts(fp.items);
            setActivity(act.items);
          }
          setView('complete');
        } else if (next.status === 'failed') {
          setError(next.errorMessage ?? t('errors.generic'));
          setView('pre');
        }
      } catch (e) {
        setError((e as Error).message);
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [view, job, t]);

  const startScan = useCallback(async () => {
    setStartBusy(true);
    setError(null);
    try {
      const j = await api.post<{ jobId: string; tier: string; maxProducts: number }>(
        '/api/scan/start',
      );
      setJob({
        jobId: j.jobId,
        status: 'running',
        progress: 0,
        total: 100,
        phase: null,
        phaseCounts: null,
        scanResultId: null,
        errorMessage: null,
      });
      setScanStartTime(Date.now());
      setView('scanning');
    } catch (e) {
      if (e instanceof PaywallError) {
        navigate('/plans');
      } else if (e instanceof ConflictError) {
        setError(t('errors.scanInProgress'));
      } else {
        setError((e as Error).message);
      }
    } finally {
      setStartBusy(false);
    }
  }, [t, navigate]);

  const downloadPdf = useCallback(async () => {
    if (!scan) return;
    setError(null);
    try {
      await api.download(`/api/report/${scan.id}/pdf`, `compliance-report.pdf`);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [scan]);

  // ── Loading ──
  if (view === 'loading') {
    return (
      <AppPage title={t('app.title')}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spinner accessibilityLabel="Loading" size="large" />
        </div>
      </AppPage>
    );
  }

  // ── Scanning (design #02) ──
  if (view === 'scanning' && job) {
    return <ScanningView job={job} billing={billing} />;
  }

  // ── Scan complete (design #03) ──
  if (view === 'complete' && scan) {
    const elapsed = Math.round((Date.now() - scanStartTime) / 1000);
    return (
      <ScanCompleteView
        scan={scan}
        findings={findings}
        elapsed={elapsed}
        onViewResults={() => setView('results')}
      />
    );
  }

  // ── Results (design #04/#05) ──
  if (view === 'results' && scan && billing) {
    return (
      <ResultsView
        scan={scan}
        previous={previous}
        newProductCount={newProductCount}
        billing={billing}
        findings={findings}
        fixedProducts={fixedProducts}
        activity={activity}
        totalProducts={totalProducts}
        error={error}
        onClearError={() => setError(null)}
        onRescan={() => void startScan()}
        onDownloadPdf={downloadPdf}
        onBulkFix={() => navigate(`/bulk-fix/${scan.id}`)}
        onUpgrade={() => navigate('/plans')}
        onOpenIssue={(id) => navigate(`/issue/${id}`)}
      />
    );
  }

  // ── Pre-scan / First-time (design #01) ──
  return <PreScanView billing={billing} error={error} startBusy={startBusy} onClearError={() => setError(null)} onStartScan={() => void startScan()} />;
}


// ═══════════════════════════════════════════════
// Pre-scan view (design #01)
// ═══════════════════════════════════════════════
function BluePrimaryButton({ children, loading, onClick }: { children: React.ReactNode; loading?: boolean; onClick: () => void }): JSX.Element {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      disabled={loading}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: hover ? '#004BB0' : '#005BD3',
        color: '#fff',
        border: 'none',
        borderRadius: 8,
        padding: '10px 24px',
        fontSize: 14,
        fontWeight: 600,
        cursor: loading ? 'default' : 'pointer',
        fontFamily: 'inherit',
        opacity: loading ? 0.7 : 1,
        transition: 'background 0.15s',
      }}
    >
      {loading ? 'Loading…' : children}
    </button>
  );
}

/** Blue button — design .btn-primary. size: sm (plan bar), md (default), lg (CTA) */
function BlueBtn({ children, onClick, size = 'md' }: { children: React.ReactNode; onClick: () => void; size?: 'sm' | 'md' | 'lg' }): JSX.Element {
  const [hover, setHover] = useState(false);
  const padding = size === 'sm' ? '5px 14px' : size === 'lg' ? '10px 24px' : '8px 18px';
  const fontSize = size === 'sm' ? 12 : size === 'lg' ? 14 : 13;
  return (
    <button type="button" onClick={onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ background: hover ? '#004BB0' : '#005BD3', color: '#fff', border: 'none', borderRadius: 8, padding, fontSize, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'background 0.15s' }}>
      {children}
    </button>
  );
}

/** Outline button — design .btn. size: sm (table header), md (default) */
function OutlineBtn({ children, onClick, size = 'md' }: { children: React.ReactNode; onClick: () => void; size?: 'sm' | 'md' }): JSX.Element {
  const [hover, setHover] = useState(false);
  const padding = size === 'sm' ? '4px 12px' : '8px 18px';
  const fontSize = size === 'sm' ? 12 : 13;
  return (
    <button type="button" onClick={onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ background: hover ? '#F6F6F7' : '#fff', color: '#303030', border: '1px solid #C9CCCF', borderRadius: 8, padding, fontSize, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'background 0.15s' }}>
      {children}
    </button>
  );
}

function PreScanView({
  billing,
  error,
  startBusy,
  onClearError,
  onStartScan,
}: {
  billing: BillingStatus | null;
  error: string | null;
  startBusy: boolean;
  onClearError: () => void;
  onStartScan: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const daysLeft = daysUntilDeadline();
  const maxProducts = billing ? (billing.plan === 'free' ? 25 : billing.plan === 'full' ? 500 : 1000) : 25;
  const [totalProducts, setTotalProducts] = useState<number | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    api.get<{ count: number | null }>('/api/shop/product-count', ac.signal)
      .then((r) => { if (r.count !== null) setTotalProducts(r.count); })
      .catch(() => {});
    return () => ac.abort();
  }, []);

  return (
    <AppPage title={t('app.title')}>
      <BlockStack gap="400">
        {error && <Banner tone="critical" onDismiss={onClearError}>{error}</Banner>}

        {/* EU deadline bar */}
        {daysLeft > 0 && (
          <div style={{
            background: '#FFF4F4',
            border: '1px solid #E4A5A5',
            borderRadius: 12,
            padding: '16px 20px',
          }}>
            <InlineStack gap="400" blockAlign="center" wrap={false}>
              <div>
                <div style={{ fontSize: 36, fontWeight: 700, color: '#D72C0D', lineHeight: 1 }}>{daysLeft}</div>
                <div style={{ fontSize: 11, color: '#D72C0D', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
                  {t('preScan.daysLeft')}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <Text as="p" variant="bodySm" fontWeight="semibold" tone="critical">
                  {t('preScan.deadlineTitle')}
                </Text>
                <Text as="p" variant="bodySm" tone="critical">
                  {t('preScan.deadlineDesc')}
                </Text>
              </div>
            </InlineStack>
          </div>
        )}

        {/* Main CTA card */}
        <Card>
          <div style={{ textAlign: 'center', padding: '28px 20px 24px' }}>
            <div style={{
              width: 52, height: 52, background: '#DBEAFE', borderRadius: '50%',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14,
            }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#005BD3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 6 }}>
              {t('preScan.heading')}
            </div>
            <div style={{ fontSize: 13, color: 'var(--p-color-text-subdued)', maxWidth: 460, margin: '0 auto 18px', lineHeight: 1.6 }}>
              {t('preScan.body')}
            </div>
            <BluePrimaryButton loading={startBusy} onClick={onStartScan}>
              {t('preScan.startFree')}
            </BluePrimaryButton>
            <div style={{ fontSize: 12, color: 'var(--p-color-text-subdued)', marginTop: 10 }}>
              {t('preScan.subtext', { limit: maxProducts })}
            </div>
          </div>
        </Card>

        {/* 3-step grid */}
        <InlineGrid columns={{ xs: 1, sm: 3 }} gap="300">
          {(['scan', 'review', 'fix'] as const).map((step, i) => (
            <div key={step} style={{ background: 'var(--p-color-bg-surface-secondary)', borderRadius: 8, padding: 16 }}>
              <div style={{
                width: 22, height: 22, borderRadius: '50%', background: '#DBEAFE', color: '#1E40AF',
                fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8,
              }}>
                {i + 1}
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                {t(`preScan.step${step.charAt(0).toUpperCase() + step.slice(1)}Title`)}
              </div>
              <div style={{ fontSize: 12, color: 'var(--p-color-text-subdued)', lineHeight: 1.5 }}>
                {t(`preScan.step${step.charAt(0).toUpperCase() + step.slice(1)}Desc`)}
              </div>
            </div>
          ))}
        </InlineGrid>

        {/* Store info card */}
        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">{t('preScan.yourStore')}</Text>

            {/* Stats bar */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1,
              background: 'var(--p-color-border)', borderRadius: 8, overflow: 'hidden',
            }}>
              <StatsCell value={totalProducts !== null ? String(totalProducts) : '—'} label={t('preScan.totalProducts')} />
              <StatsCell value="0" label={t('preScan.scanned')} />
              <StatsCell value="?" label={t('preScan.complianceIssues')} subdued />
            </div>

            {/* Selling regions */}
            <Text as="h3" variant="headingSm">{t('preScan.regionsDetected')}</Text>
            <RegionRow flag="EU" flagBg="#003399" name={t('settings.regionEU')} source={t('preScan.detectedFromMarkets')} tag={t('settings.tagEU')} tone="critical" />
            <RegionRow flag="US" flagBg="#B22234" name={t('settings.regionUS')} source={t('preScan.detectedFromMarkets')} tag={t('settings.tagFTC')} tone="warning" />
            <RegionRow flag="NY" flagBg="#1E3A5F" name={t('settings.regionNY')} source={t('preScan.inferredUS')} tag={t('settings.tagNY')} tone="info" />

            <div style={{ height: 1, background: '#F3F4F6' }} />
            <Text as="p" variant="bodySm" tone="subdued">
              {t('preScan.regionsNotePrefix')}{' '}
              <Button variant="plain" size="micro" onClick={() => navigate('/settings')}>{t('preScan.regionsNoteLink')}</Button>
            </Text>
          </BlockStack>
        </Card>

        {/* Regulations card */}
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">{t('preScan.regulationsTitle')}</Text>
            <RegulationInfo
              color="#D72C0D"
              title={t('preScan.regEuTitle')}
              desc={t('preScan.regEuDesc')}
              date={t('preScan.regEuDate')}
              dateColor="#D72C0D"
            />
            <RegulationInfo
              color="#B98900"
              title={t('preScan.regFtcTitle')}
              desc={t('preScan.regFtcDesc')}
              date={t('preScan.regFtcDate')}
              dateColor="#B98900"
            />
            <RegulationInfo
              color="#005BD3"
              title={t('preScan.regNyTitle')}
              desc={t('preScan.regNyDesc')}
              date={t('preScan.regNyDate')}
              dateColor="#005BD3"
            />
          </BlockStack>
        </Card>

        {/* Bottom CTA */}
        <Card>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
              {t('preScan.bottomCtaTitle')}
            </div>
            <div style={{ fontSize: 13, color: 'var(--p-color-text-subdued)', marginBottom: 14 }}>
              {t('preScan.bottomCtaBody')}
            </div>
            <BluePrimaryButton loading={startBusy} onClick={onStartScan}>
              {t('preScan.startFree')}
            </BluePrimaryButton>
          </div>
        </Card>
      </BlockStack>
    </AppPage>
  );
}


// ═══════════════════════════════════════════════
// Scanning view (design #02)
// ═══════════════════════════════════════════════
function ScanningView({
  job,
  billing,
}: {
  job: ScanJob;
  billing: BillingStatus | null;
}): JSX.Element {
  const { t } = useTranslation();
  const pct = Math.max(0, Math.min(100, job.progress));
  const maxProducts = billing ? (billing.plan === 'free' ? 25 : billing.plan === 'full' ? 500 : 1000) : 25;
  const crawled = job.phaseCounts?.crawl ?? 0;
  const productCount = crawled > 0 ? crawled : maxProducts;

  return (
    <AppPage title={t('dashboard.scanning')}>
      <Card>
        <div style={{ textAlign: 'center', padding: '48px 20px 40px' }}>
          <BlueSpinner />
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
            {t('scanning.analyzing', { count: productCount })}
          </div>
          <div style={{ fontSize: 13, color: 'var(--p-color-text-subdued)', marginBottom: 20 }}>
            {t('scanning.checkingContent')}
          </div>
          <div style={{ maxWidth: 360, margin: '0 auto 12px' }}>
            <BlueProgressBar progress={pct} />
          </div>
          <div style={{ fontSize: 13, color: '#8C8C8C' }}>
            {t('scanning.progressDetail', { done: Math.round(pct * productCount / 100), total: productCount })}
          </div>
        </div>
      </Card>
    </AppPage>
  );
}


/** Custom spinner matching design: blue (#005BD3) ring on gray border */
function BlueSpinner(): JSX.Element {
  return (
    <>
      <style>{`@keyframes bluespin { to { transform: rotate(360deg); } }`}</style>
      <div style={{
        width: 44, height: 44,
        border: '3.5px solid #E3E3E3',
        borderTopColor: '#005BD3',
        borderRadius: '50%',
        margin: '0 auto 16px',
        animation: 'bluespin 0.8s linear infinite',
      }} />
    </>
  );
}

/** Custom progress bar matching design: 8px track, blue fill */
function BlueProgressBar({ progress }: { progress: number }): JSX.Element {
  return (
    <div style={{ height: 8, background: '#F6F6F7', borderRadius: 4, overflow: 'hidden' }}>
      <div style={{
        height: '100%', borderRadius: 4, background: '#005BD3',
        width: `${Math.max(0, Math.min(100, progress))}%`,
        transition: 'width 0.5s',
      }} />
    </div>
  );
}


// ═══════════════════════════════════════════════
// Scan complete view (design #03)
// ═══════════════════════════════════════════════
function ScanCompleteView({
  scan,
  findings,
  elapsed,
  onViewResults,
}: {
  scan: ScanResult;
  findings: ComplianceIssue[];
  elapsed: number;
  onViewResults: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  // Count unique products that have issues (not raw finding count).
  const productsWithIssues = new Set(findings.map((f) => f.productId)).size;
  const passedCount = Math.max(0, scan.productCount - productsWithIssues);

  return (
    <AppPage title={t('scanComplete.title')}>
      <Card>
        <div style={{ textAlign: 'center', padding: '40px 20px 36px' }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%', background: '#D1FAE5',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#008060" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 6 }}>
            {t('scanComplete.productsScanned', { count: scan.productCount })}
          </div>
          <div style={{ fontSize: 13, color: 'var(--p-color-text-subdued)', marginBottom: 20 }}>
            {t('scanComplete.completedIn', { seconds: elapsed })}
          </div>

          {/* Stats bar */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1,
            background: 'var(--p-color-border)', borderRadius: 8, overflow: 'hidden',
            maxWidth: 420, margin: '0 auto 24px',
          }}>
            <StatsCell value={String(scan.summary.critical + scan.summary.warning + scan.summary.info)} label={t('scanComplete.issuesFound')} valueColor="#D72C0D" />
            <StatsCell value={String(passedCount)} label={t('scanComplete.passed')} valueColor="#008060" />
            <StatsCell value={String(scan.score)} label={t('scanComplete.readinessScore')} />
          </div>

          <BluePrimaryButton onClick={onViewResults}>
            {t('scanComplete.viewResults')}
          </BluePrimaryButton>
        </div>
      </Card>
    </AppPage>
  );
}


// ═══════════════════════════════════════════════
// Results view (design #04 free / #05 paid)
// ═══════════════════════════════════════════════
function ResultsView({
  scan,
  previous,
  newProductCount,
  billing,
  findings,
  fixedProducts,
  activity,
  totalProducts,
  error,
  onClearError,
  onRescan,
  onDownloadPdf,
  onBulkFix,
  onUpgrade,
  onOpenIssue,
}: {
  scan: ScanResult;
  previous: { score: number; createdAt: string } | null;
  newProductCount: number;
  billing: BillingStatus;
  findings: ComplianceIssue[];
  fixedProducts: FixedProduct[];
  activity: ActivityItem[];
  totalProducts: number | null;
  error: string | null;
  onClearError: () => void;
  onRescan: () => void;
  onDownloadPdf: () => void;
  onBulkFix: () => void;
  onUpgrade: () => void;
  onOpenIssue: (issueId: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const isFree = billing.plan === 'free';
  const isMonitoring = billing.plan === 'monitoring';
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [showAllPassed, setShowAllPassed] = useState(false);
  const [unscannedSample, setUnscannedSample] = useState<{ id: string; title: string }[]>([]);
  const daysLeft = daysUntilDeadline();

  const groups = useMemo(() => groupFindingsByProduct(findings), [findings]);

  // `fixedProducts` (from /fixed-products) is the source of truth for which
  // products currently carry a disclosure — survives a rescan where the
  // product now passes and drops out of `findings`. Use it to filter both
  // unfixed issue rows and the passed list so a fixed product only appears
  // in one bucket (the Fixed tab / row).
  const fixedProductIds = useMemo(
    () => new Set(fixedProducts.map((p) => p.productId)),
    [fixedProducts],
  );

  // Derive passed products from scannedProducts minus products with issues
  // AND minus products that are "fixed" (disclosure applied). Fixed products
  // may pass on a rescan, but they belong in the Fixed row — not Passed.
  const issueProductIds = useMemo(() => new Set(groups.map((g) => g.productId)), [groups]);

  // productId → Shopify Product.createdAt (ISO). Drives "newest first" sort
  // across all three product tables (issues, passed, fixed). Older scans may
  // lack createdAt — those rows fall back to the numeric portion of the
  // Shopify GID, which is monotonically increasing per shop.
  const productCreatedAt = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of scan.scannedProducts ?? []) {
      if (p.createdAt) m.set(p.productId, p.createdAt);
    }
    return m;
  }, [scan.scannedProducts]);

  const compareNewer = useMemo(() => {
    const dateOf = (id: string, fallback?: string) => productCreatedAt.get(id) ?? fallback ?? '';
    return (a: { productId: string; createdAt?: string }, b: { productId: string; createdAt?: string }) => {
      const da = dateOf(a.productId, a.createdAt);
      const db = dateOf(b.productId, b.createdAt);
      if (da && db) return db.localeCompare(da);
      if (da) return -1;
      if (db) return 1;
      return gidNumeric(b.productId) - gidNumeric(a.productId);
    };
  }, [productCreatedAt]);

  const passedProducts = useMemo(
    () =>
      (scan.scannedProducts ?? [])
        .filter((p) => !issueProductIds.has(p.productId) && !fixedProductIds.has(p.productId))
        .sort(compareNewer),
    [scan.scannedProducts, issueProductIds, fixedProductIds, compareNewer],
  );

  // Fixed-products list is fetched from /fixed-products which orders by
  // disclosure-applied date. Re-sort by Shopify product createdAt so the
  // table follows the same "newest product first" convention as the others.
  const sortedFixedProducts = useMemo(
    () => [...fixedProducts].sort(compareNewer),
    [fixedProducts, compareNewer],
  );
  const fixedCount = fixedProducts.length;

  // Set of product IDs that have at least one info-severity finding
  const productsWithInfo = useMemo(() => {
    const ids = new Set<string>();
    for (const f of findings) { if (f.severity === 'info') ids.add(f.productId); }
    return ids;
  }, [findings]);

  const filteredGroups = useMemo(() => {
    // Unfixed issue rows: always exclude products that have a live disclosure
    // (fixedProductIds), since those render as FixedProductRow instead.
    const unfixed = groups.filter((g) => !fixedProductIds.has(g.productId));
    // Re-sort within severity buckets so newer products surface first inside
    // the same severity. groupFindingsByProduct already orders by severity
    // desc; we keep severity primary and use createdAt desc as the tie-break.
    const SEV_RANK = { critical: 3, warning: 2, info: 1 } as const;
    const sorted = [...unfixed].sort((a, b) => {
      const sev = SEV_RANK[b.severity] - SEV_RANK[a.severity];
      if (sev !== 0) return sev;
      return compareNewer(a, b);
    });
    if (activeFilter === 'all') return sorted;
    if (activeFilter === 'critical') return sorted.filter((g) => g.severity === 'critical');
    if (activeFilter === 'warning') return sorted.filter((g) => g.severity === 'warning');
    // Info filter: show any product that has info findings (even if group severity is higher)
    if (activeFilter === 'info') return sorted.filter((g) => productsWithInfo.has(g.productId));
    // Passed + fixed are rendered separately via their own collections.
    if (activeFilter === 'passed' || activeFilter === 'fixed') return [];
    return sorted;
  }, [groups, fixedProductIds, activeFilter, productsWithInfo, compareNewer]);
  const issueCount = scan.summary.critical + scan.summary.warning;
  const passedCount = passedProducts.length;
  const storeTotal = totalProducts ?? scan.productCount;
  const unscannedCount = Math.max(0, storeTotal - scan.productCount);
  const pct = billing.usageLimit > 0 ? Math.min(100, Math.round((billing.usageCount / billing.usageLimit) * 100)) : 0;
  const fullPrice = billing.pricing.full.price;
  // Can rescan? free=no, full=max 2 scans (1 initial + 1 re-scan), monitoring=unlimited
  const canRescan = billing.maxScans === null
    ? true // unlimited (monitoring)
    : billing.scanCount < billing.maxScans;

  // Score trend card — matches design/screens/05-results-paid.html line 40
  // exactly: `↑ 58 → 65` in --success green when improving, `↓ 65 → 58` in
  // --critical red when declining, and a neutral baseline on the first scan.
  // Palette tokens come from design/styles.css (--success #008060,
  // --critical #D72C0D, --warning #B98900) — hardcoded here rather than via
  // CSS vars because MonitoringItem takes a raw color string.
  const scoreTrend = useMemo(() => {
    if (!previous) {
      // No prior scan to compare — show the current score as baseline in
      // neutral grey so we don't fake a green "improving" signal.
      return {
        value: `${scan.score}`,
        color: '#6D7175',
        sub: t('results.scoreBaseline'),
      };
    }
    const diff = scan.score - previous.score;
    // Weeks between scans, rounded, floored at 1 so same-day re-scans still
    // read naturally ("over 1 week") instead of "over 0 weeks".
    const weeks = Math.max(
      1,
      Math.round((new Date(scan.createdAt).getTime() - new Date(previous.createdAt).getTime()) / (7 * 24 * 60 * 60_000)),
    );
    if (diff > 0) {
      return {
        value: `↑ ${previous.score} → ${scan.score}`,
        color: '#008060',
        sub: t('results.improvingOverWeeks', { count: weeks }),
      };
    }
    if (diff < 0) {
      return {
        value: `↓ ${previous.score} → ${scan.score}`,
        color: '#D72C0D',
        sub: t('results.decliningOverWeeks', { count: weeks }),
      };
    }
    return {
      value: `${previous.score} → ${scan.score}`,
      color: '#6D7175',
      sub: t('results.noChangeOverWeeks', { count: weeks }),
    };
  }, [scan.score, scan.createdAt, previous, t]);

  // Fetch a few unscanned product names for ghost rows (free tier)
  useEffect(() => {
    if (!isFree || unscannedCount === 0) return;
    const scannedIds = (scan.scannedProducts ?? []).map((p) => p.productId).join(',');
    const ac = new AbortController();
    api.get<{ products: { id: string; title: string }[] }>(
      `/api/shop/products-sample?exclude=${encodeURIComponent(scannedIds)}&limit=3`,
      ac.signal,
    )
      .then((r) => setUnscannedSample(r.products))
      .catch(() => {});
    return () => ac.abort();
  }, [isFree, unscannedCount, scan.scannedProducts]);

  return (
    <AppPage>
      {/* Title row — design .title-row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>{t('dashboard.resultsTitle')}</div>
          <div style={{ fontSize: 13, color: '#6D7175' }}>
            {t('dashboard.resultsSubtitle', { count: scan.productCount, total: storeTotal, when: new Date(scan.createdAt).toLocaleString() })}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {!isFree && issueCount > 0 && (
            <BlueBtn onClick={onBulkFix}>{t('results.bulkFixN', { count: issueCount })}</BlueBtn>
          )}
          <OutlineBtn onClick={onDownloadPdf}>{t('dashboard.downloadPdf')}</OutlineBtn>
          {canRescan ? (
            <OutlineBtn onClick={onRescan}>{t('dashboard.rescan')}</OutlineBtn>
          ) : (
            <OutlineBtn onClick={onUpgrade}>{t('dashboard.upgradeToRescan')}</OutlineBtn>
          )}
        </div>
      </div>

      <BlockStack gap="300">
        {error && <Banner tone="critical" onDismiss={onClearError}>{error}</Banner>}

        {/* Plan bar — design .plan-bar */}
        <div style={{ padding: '12px 16px', background: '#fff', borderRadius: 12, border: '1px solid #E3E3E3' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              {isMonitoring ? (
                <>
                  <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: '#D1FAE5', color: '#065F46' }}>
                    Monitoring plan
                  </span>
                  <span style={{ fontWeight: 600 }}>${billing.pricing.monitoring.price}/mo</span>
                  <span style={{ color: '#6D7175' }}>·</span>
                  <span style={{ color: '#6D7175' }}>{scan.productCount} of {billing.usageLimit.toLocaleString()} products used</span>
                  <div style={{ width: 140, height: 6, background: '#F6F6F7', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 3, background: '#008060', width: `${Math.min(100, Math.round((scan.productCount / billing.usageLimit) * 100))}%`, transition: 'width 0.3s' }} />
                  </div>
                </>
              ) : (
                <>
                  <span style={{ fontWeight: 600 }}>{isFree ? 'Free plan' : 'Full report'}</span>
                  {!isFree && <span style={{ fontWeight: 600 }}>${fullPrice}</span>}
                  <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: '#DBEAFE', color: '#1E40AF' }}>
                    {billing.usageLimit} product limit
                  </span>
                  <div style={{ width: 140, height: 6, background: '#F6F6F7', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 3, background: '#005BD3', width: `${Math.min(100, Math.round((scan.productCount / billing.usageLimit) * 100))}%`, transition: 'width 0.3s' }} />
                  </div>
                </>
              )}
            </div>
            <div style={{ fontSize: 13, color: '#6D7175', display: 'flex', alignItems: 'center', gap: 8 }}>
              {isMonitoring ? (
                <span>Next auto-scan: {t('results.nextWeek')}</span>
              ) : (
                <>
                  <span>{scan.productCount} of {billing.usageLimit} scans used</span>
                  {isFree && storeTotal > billing.usageLimit && (
                    <BlueBtn size="sm" onClick={onUpgrade}>{`Scan all ${storeTotal} products — $${fullPrice}`}</BlueBtn>
                  )}
                  {!isFree && !isMonitoring && (
                    <BlueBtn size="sm" onClick={onUpgrade}>{`Upgrade to monitoring — $${billing.pricing.monitoring.price}/mo`}</BlueBtn>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Free tier: unscanned warning — design .banner-warning */}
        {isFree && unscannedCount > 0 && (
          <div style={{ background: '#FFF8E6', border: '1px solid #E0C878', borderRadius: 12, padding: '14px 16px' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#7E5E09', marginBottom: 4 }}>
              {t('results.unscannedTitle', { count: unscannedCount })}
            </div>
            <div style={{ fontSize: 13, color: '#7E5E09', lineHeight: 1.5, marginBottom: 12 }}>
              {t('results.unscannedBody')}
            </div>
            <div style={{ display: 'flex', gap: 24, marginBottom: 14, flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#7E5E09' }}>{scan.productCount}</div>
                <div style={{ fontSize: 11, color: '#7E5E09', opacity: 0.8 }}>{t('results.scannedLabel')}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#7E5E09' }}>{unscannedCount}</div>
                <div style={{ fontSize: 11, color: '#7E5E09', opacity: 0.8 }}>{t('results.notScannedLabel')}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#7E5E09' }}>?</div>
                <div style={{ fontSize: 11, color: '#7E5E09', opacity: 0.8 }}>{t('results.hiddenIssues')}</div>
              </div>
            </div>
            <BlueBtn onClick={onUpgrade}>{t('results.scanAllCta', { price: fullPrice })}</BlueBtn>
          </div>
        )}

        {/* Score ring — design .score-row */}
        <Card>
          <ScoreRing
            score={scan.score}
            title={t('dashboard.readinessCardTitle')}
            detail={`${scan.summary.critical} ${t('severity.critical')} · ${scan.summary.warning} ${t('severity.warning')} · ${scan.summary.info} ${t('severity.info')} · ${passedCount} passed`}
            disclaimer={isFree && unscannedCount > 0 ? `${t('results.freeDisclaimer', { count: scan.productCount })} ${unscannedCount} not yet checked.` : undefined}
            deadline={daysLeft > 0 ? t('dashboard.daysUntil', { days: daysLeft }) : undefined}
          />
        </Card>

        {/* 4-column metrics — design .metrics-grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
          <MetricCard label={t('severity.critical')} value={scan.summary.critical} sub={t('results.mustFix')} tone="critical" />
          <MetricCard label={t('results.warnings')} value={scan.summary.warning} sub={t('results.shouldReview')} tone="warning" />
          <MetricCard label={t('severity.info')} value={scan.summary.info} sub={t('results.lowPriority')} tone="subdued" />
          <MetricCard label={t('results.passedLabel')} value={passedCount} sub={t('results.noIssues')} tone="success" />
        </div>

        {/* Monitoring status — design .monitoring-grid (monitoring tier only) */}
        {isMonitoring && (
          <Card>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>{t('results.monitoringStatus')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <MonitoringItem label={t('results.lastFullScan')} value={new Date(scan.createdAt).toLocaleDateString()} sub={t('results.productsChecked', { count: scan.productCount })} />
              <MonitoringItem label={t('results.nextAutoScan')} value={t('results.nextWeek')} sub={t('results.weeklySchedule')} />
              <MonitoringItem
                label="New products since last scan"
                value={`${newProductCount} added`}
                valueColor={newProductCount > 0 ? '#B98900' : undefined}
                sub="Auto-scanned on upload"
              />
              <MonitoringItem label={t('results.scoreTrend')} value={scoreTrend.value} valueColor={scoreTrend.color} sub={scoreTrend.sub} />
            </div>
          </Card>
        )}

        {/* Regulation bars */}
        <RegulationBreakdown regulations={scan.summary.regulations} />

        {/* Product table */}
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              {isFree ? t('results.scannedProducts', { count: scan.productCount }) : t('results.allProducts', { count: storeTotal })}
            </div>
            {isFree ? (
              issueCount > 0 ? (
                <OutlineBtn size="sm" onClick={onBulkFix}>{t('results.bulkFixN', { count: issueCount })}</OutlineBtn>
              ) : null
            ) : (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {['all', 'critical', 'warning', 'info', 'passed', 'fixed'].map((f) => (
                  <FilterButton
                    key={f}
                    label={filterLabelEx(f, scan, passedCount, fixedCount, t)}
                    active={activeFilter === f}
                    onClick={() => { setActiveFilter(f); if (f === 'passed') setShowAllPassed(true); }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Table header */}
          <div style={{ display: 'flex', padding: '8px 0', borderBottom: '2px solid #E3E3E3', fontSize: 12, fontWeight: 600, color: '#6D7175', gap: 10 }}>
            <div style={{ flex: 3 }}>{t('dashboard.productCol')}</div>
            {!isFree && <div style={{ flex: 1.2 }}>{t('dashboard.contentCol')}</div>}
            <div style={{ flex: 0.6, textAlign: 'center' }}>{t('dashboard.riskCol')}</div>
            <div style={{ flex: 0.8, textAlign: 'center' }}>{t('dashboard.statusCol')}</div>
            <div style={{ flex: 1.3 }}>{t('dashboard.regulationCol')}</div>
            <div style={{ flex: 0.6 }} />
          </div>

          {/* Issue rows (unfixed) FIRST — newest products with live issues
              must surface above already-fixed rows so the merchant's
              attention lands on what still needs action. Sorted by severity
              desc, then createdAt desc inside each bucket. */}
          {activeFilter !== 'passed' && activeFilter !== 'fixed' && filteredGroups.map((g) => (
            <ProductRow
              key={g.productId}
              group={g}
              showContent={!isFree}
              onClick={() => onOpenIssue(g.representativeFindingId)}
              t={t}
            />
          ))}

          {/* Fixed rows — render AFTER unfixed issues. Driven by
              fixedProducts, which includes products that currently pass a
              rescan thanks to their disclosure (they'd have dropped out of
              `groups` otherwise). All tiers render these — otherwise on Free
              an applied product would vanish from the table. */}
          {(activeFilter === 'all' || activeFilter === 'fixed') && sortedFixedProducts.length > 0 && (
            sortedFixedProducts.map((p) => (
              <FixedProductRow
                key={p.productId}
                product={p}
                showContent={!isFree}
              />
            ))
          )}

          {activeFilter === 'fixed' && fixedProducts.length === 0 && (
            <div style={{ padding: '16px 0', textAlign: 'center', fontSize: 13, color: '#6D7175' }}>
              {t('dashboard.noIssues')}
            </div>
          )}

          {activeFilter !== 'passed' && activeFilter !== 'fixed' && filteredGroups.length === 0 && fixedProducts.length === 0 && (
            <div style={{ padding: '16px 0', textAlign: 'center', fontSize: 13, color: '#6D7175' }}>
              {t('dashboard.noIssues')}
            </div>
          )}

          {/* ── Free tier: individual passed product rows ── */}
          {isFree && activeFilter === 'all' && passedProducts.map((p) => (
            <div
              key={p.productId}
              style={{
                display: 'flex', alignItems: 'center', padding: '11px 0',
                borderBottom: '1px solid #F3F4F6', fontSize: 13, gap: 10,
                opacity: 0.5, cursor: 'default',
              }}
            >
              <div style={{ flex: 3, fontWeight: 500 }}>{p.productTitle}</div>
              <div style={{ flex: 0.6, textAlign: 'center', fontWeight: 700, color: '#008060' }}>{p.maxAiScore}</div>
              <div style={{ flex: 0.8, textAlign: 'center' }}>
                <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#D1FAE5', color: '#065F46' }}>Pass</span>
              </div>
              <div style={{ flex: 1.3, fontSize: 12, color: '#6D7175' }}>—</div>
              <div style={{ flex: 0.6 }} />
            </div>
          ))}

          {/* ── Paid tier: passed summary or individual rows ── */}
          {!isFree && passedCount > 0 && (activeFilter === 'all' || activeFilter === 'passed') && (
            <>
              {activeFilter === 'passed' || showAllPassed ? (
                passedProducts.map((p) => (
                  <div
                    key={p.productId}
                    style={{
                      display: 'flex', alignItems: 'center', padding: '11px 0',
                      borderBottom: '1px solid #F3F4F6', fontSize: 13, gap: 10,
                      opacity: 0.5, cursor: 'default',
                    }}
                  >
                    <div style={{ flex: 3, fontWeight: 500 }}>{p.productTitle}</div>
                    <div style={{ flex: 1.2, fontSize: 12, color: '#6D7175' }}>—</div>
                    <div style={{ flex: 0.6, textAlign: 'center', fontWeight: 700, color: '#008060' }}>{p.maxAiScore}</div>
                    <div style={{ flex: 0.8, textAlign: 'center' }}>
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#D1FAE5', color: '#065F46' }}>Pass</span>
                    </div>
                    <div style={{ flex: 1.3, fontSize: 12, color: '#6D7175' }}>—</div>
                    <div style={{ flex: 0.6 }} />
                  </div>
                ))
              ) : (
                <div style={{
                  background: '#F6F6F7', borderRadius: 8, padding: '12px 16px', marginTop: 12,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
                }}>
                  <div>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{passedCount} products passed</span>
                    <span style={{ fontSize: 12, color: '#6D7175', marginLeft: 6 }}>No issues detected</span>
                  </div>
                  <FilterButton label="Show all passed →" active={false} onClick={() => setShowAllPassed(true)} />
                </div>
              )}
            </>
          )}

          {/* ── Free tier: not scanned section ── */}
          {isFree && unscannedCount > 0 && (
            <>
              <div style={{ borderTop: '2px solid #E3E3E3', marginTop: 8, paddingTop: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6D7175', marginBottom: 8 }}>
                  Not scanned ({unscannedCount} products)
                </div>
              </div>
              {/* Ghost rows for unscanned products */}
              {unscannedSample.map((p) => (
                <div
                  key={p.id}
                  style={{
                    display: 'flex', alignItems: 'center', padding: '11px 0',
                    borderBottom: '1px solid #F3F4F6', fontSize: 13, gap: 10,
                    opacity: 0.35, cursor: 'default',
                  }}
                >
                  <div style={{ flex: 3, fontWeight: 500 }}>{p.title}</div>
                  <div style={{ flex: 0.6, textAlign: 'center', color: '#6D7175' }}>—</div>
                  <div style={{ flex: 0.8, textAlign: 'center' }}>
                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#F6F6F7', color: '#6D7175' }}>Not scanned</span>
                  </div>
                  <div style={{ flex: 1.3 }} />
                  <div style={{ flex: 0.6 }} />
                </div>
              ))}
              {unscannedCount > unscannedSample.length && (
                <div style={{ textAlign: 'center', padding: '14px 0 4px' }}>
                  <span style={{ fontSize: 13, color: '#6D7175' }}>+ {unscannedCount - unscannedSample.length} more unscanned products</span>
                  <div style={{ marginTop: 10 }}>
                    <BlueBtn onClick={onUpgrade}>{`Scan all ${storeTotal} products — $${fullPrice}`}</BlueBtn>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>

        {/* Recent activity — monitoring tier only (design #05) */}
        {isMonitoring && <RecentActivityCard items={activity} />}
      </BlockStack>
    </AppPage>
  );
}


// ═══════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════

function ProductRow({
  group: g,
  showContent,
  onClick,
  t,
}: {
  group: ReturnType<typeof groupFindingsByProduct>[number];
  showContent: boolean;
  onClick?: () => void;
  t: (k: string, o?: Record<string, unknown>) => string;
}): JSX.Element {
  const band = riskBandKey(g.maxScore);
  const riskColor = band === 'high' ? '#D72C0D' : band === 'medium' ? '#B98900' : 'var(--p-color-text-subdued)';
  const contentLabel = g.contentTypes.map((c) => contentTypeLabel(c, t)).join(', ');
  const regLabel = formatPublicRegulations(g.regulations, t);

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', padding: '11px 0',
        borderBottom: '1px solid #F3F4F6', fontSize: 13, gap: 10,
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div style={{ flex: 3, fontWeight: 500 }}>
        {onClick ? (
          <span style={{ color: 'var(--p-color-text-interactive)' }}>{g.productTitle}</span>
        ) : (
          g.productTitle
        )}
      </div>
      {showContent && <div style={{ flex: 1.2, fontSize: 12, color: 'var(--p-color-text-subdued)' }}>{contentLabel}</div>}
      <div style={{ flex: 0.6, textAlign: 'center', fontWeight: 700, color: riskColor }}>{g.maxScore}</div>
      <div style={{ flex: 0.8, textAlign: 'center' }}>
        <SeverityBadge severity={g.severity} />
        {g.subordinateInfoCount > 0 && (
          <div style={{ fontSize: 10, color: '#6D7175', marginTop: 2 }}>
            {t('dashboard.plusInfoCount', { count: g.subordinateInfoCount })}
          </div>
        )}
      </div>
      <div style={{ flex: 1.3, fontSize: 12, color: 'var(--p-color-text-subdued)' }}>{regLabel}</div>
      <div style={{ flex: 0.6, textAlign: 'right' }}>
        {onClick && <span style={{ color: '#005BD3', fontSize: 12, fontWeight: 600 }}>View →</span>}
      </div>
    </div>
  );
}

/**
 * Fixed product row — design .product-row-fixed "88 → 22 · Fixed".
 *
 *   - originalScore = pre-fix AI-detection probability snapshotted at apply
 *     time (backup.preFixMaxAiScore).
 *   - currentScore  = post-disclosure projected risk. Server applies the
 *     DISCLOSED_RISK_MULTIPLIER so the trend renders immediately without
 *     waiting for the next rescan.
 *
 * No View action: once fixed there's nothing to do on the detail page — the
 * merchant already resolved the issue, and showing "View →" implies another
 * action is required. Dashboard clicks for fixed products are a no-op.
 */
function FixedProductRow({
  product: p,
  showContent,
}: {
  product: FixedProduct;
  showContent: boolean;
}): JSX.Element {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', padding: '11px 0',
        borderBottom: '1px solid #F3F4F6', fontSize: 13, gap: 10,
        opacity: 0.75,
      }}
    >
      <div style={{ flex: 3, fontWeight: 500 }}>{p.productTitle}</div>
      {showContent && <div style={{ flex: 1.2, fontSize: 12, color: '#6D7175' }}>Description</div>}
      <div style={{ flex: 0.6, textAlign: 'center', fontWeight: 700, color: '#008060' }}>
        {p.originalScore} → {p.currentScore}
      </div>
      <div style={{ flex: 0.8, textAlign: 'center' }}>
        <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#D1FAE5', color: '#065F46' }}>Fixed</span>
      </div>
      <div style={{ flex: 1.3, fontSize: 12, color: '#6D7175' }}>Disclosure applied</div>
      <div style={{ flex: 0.6 }} />
    </div>
  );
}

/**
 * Recent activity feed. Server ships a flat time-ordered list; this component
 * does the UX work of grouping + collapsing so a busy day doesn't flood the
 * card with 40 near-identical "Disclosure applied" lines.
 *
 * Rules:
 *   - Group by calendar day (local). Most-recent day labeled "Today",
 *     yesterday as "Yesterday", older as "MMM D".
 *   - Within a day, keep scans (manual / weekly) individually — they're
 *     infrequent and each one is a distinct milestone.
 *   - Collapse webhook auto-scans, disclosure_applied, and rewrite_applied
 *     when ≥3 of the same kind land in the same day. The summary shows
 *     count + aggregate signal (avg score trend or worst severity).
 *   - Hard cap at 8 lines per day so a pathological day can't blow up the
 *     card; the rest hide behind "+N more".
 */
function RecentActivityCard({ items }: { items: ActivityItem[] }): JSX.Element {
  if (items.length === 0) {
    return (
      <Card>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Recent activity</div>
        <div style={{ fontSize: 13, color: '#6D7175' }}>
          No recent activity yet. Scans, auto-rescans, and fixes will appear here.
        </div>
      </Card>
    );
  }

  const byDay = groupActivityByDay(items);

  return (
    <Card>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Recent activity</div>
      <div style={{ fontSize: 13, lineHeight: 1.9, color: '#6D7175' }}>
        {byDay.map(({ dayLabel, lines }) =>
          lines.length === 1 ? (
            <div key={dayLabel}>
              <span style={{ color: '#303030', fontWeight: 600 }}>{dayLabel}</span>
              {' — '}
              {lines[0]}
            </div>
          ) : (
            <div key={dayLabel} style={{ marginBottom: 6 }}>
              <div style={{ color: '#303030', fontWeight: 600 }}>{dayLabel}</div>
              {lines.map((line, i) => (
                <div key={i} style={{ paddingLeft: 14 }}>• {line}</div>
              ))}
            </div>
          ),
        )}
      </div>
    </Card>
  );
}

/** Bucket events by calendar day, then format lines per bucket. */
function groupActivityByDay(items: ActivityItem[]): Array<{ dayLabel: string; lines: string[] }> {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Preserve input order (server sends DESC by createdAt).
  const bucketsByKey = new Map<string, { date: Date; events: ActivityItem[] }>();
  const keyOrder: string[] = [];
  for (const item of items) {
    const d = new Date(item.createdAt);
    const k = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    if (!bucketsByKey.has(k)) {
      const dayStart = new Date(d);
      dayStart.setHours(0, 0, 0, 0);
      bucketsByKey.set(k, { date: dayStart, events: [] });
      keyOrder.push(k);
    }
    bucketsByKey.get(k)!.events.push(item);
  }

  const MAX_LINES_PER_DAY = 8;
  const COLLAPSE_THRESHOLD = 3;
  const COLLAPSIBLE_KINDS = new Set<ActivityItem['kind']>([
    'disclosure_applied',
    'disclosure_undone',
    'rewrite_applied',
    'scan_webhook',
  ]);

  return keyOrder.map((k) => {
    const bucket = bucketsByKey.get(k)!;
    const label =
      bucket.date.getTime() === today.getTime()
        ? 'Today'
        : bucket.date.getTime() === yesterday.getTime()
          ? 'Yesterday'
          : bucket.date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

    // Count per collapsible kind to decide whether to collapse.
    const kindCounts = new Map<ActivityItem['kind'], number>();
    for (const e of bucket.events) {
      if (COLLAPSIBLE_KINDS.has(e.kind)) {
        kindCounts.set(e.kind, (kindCounts.get(e.kind) ?? 0) + 1);
      }
    }
    const collapsed = new Set<ActivityItem['kind']>();
    for (const [kind, count] of kindCounts) {
      if (count >= COLLAPSE_THRESHOLD) collapsed.add(kind);
    }

    const lines: string[] = [];
    const renderedCollapsed = new Set<ActivityItem['kind']>();
    for (const e of bucket.events) {
      if (collapsed.has(e.kind)) {
        if (renderedCollapsed.has(e.kind)) continue;
        renderedCollapsed.add(e.kind);
        const sameKind = bucket.events.filter((x) => x.kind === e.kind);
        lines.push(formatCollapsed(e.kind, sameKind));
      } else {
        lines.push(formatActivityLine(e));
      }
    }

    if (lines.length > MAX_LINES_PER_DAY) {
      const overflow = lines.length - MAX_LINES_PER_DAY;
      lines.splice(MAX_LINES_PER_DAY);
      lines.push(`+ ${overflow} more event${overflow === 1 ? '' : 's'} this day.`);
    }
    return { dayLabel: label, lines };
  });
}

function formatActivityLine(e: ActivityItem): string {
  const title = e.productTitle ? `"${e.productTitle}"` : 'product';
  const m = e.metadata ?? {};
  switch (e.kind) {
    case 'scan_manual':
      return `Full store scan completed. ${m.productCount ?? 0} products checked. Score: ${m.score ?? 0}/100.`;
    case 'scan_weekly':
      return `Weekly auto-scan completed. ${m.productCount ?? 0} products. Score: ${m.score ?? 0}/100.`;
    case 'scan_webhook': {
      if (!m.issueCount || m.issueCount === 0) {
        return `New product ${title} auto-scanned. Result: clean.`;
      }
      const sev = m.severity ? m.severity[0]!.toUpperCase() + m.severity.slice(1) : 'Issue';
      // Compact jurisdiction labels (FTC / EU / NY) — internal signals like
      // BEST_PRACTICE_BORDERLINE filtered out so the line stays readable.
      const regs = m.regulations && m.regulations.length > 0 ? formatRegulationsShort(m.regulations) : '';
      const risk = m.originalScore != null ? `, risk ${m.originalScore}` : '';
      const detail = regs ? `${sev} (${regs}${risk})` : `${sev}${risk ? ` (risk ${m.originalScore})` : ''}`;
      return `New product ${title} auto-scanned. Result: ${detail}.`;
    }
    case 'disclosure_applied':
      if (m.originalScore != null && m.currentScore != null) {
        return `Disclosure label applied to ${title}. Score improved ${m.originalScore} → ${m.currentScore}.`;
      }
      return `Disclosure label applied to ${title}.`;
    case 'disclosure_undone':
      return `Disclosure undone on ${title}.`;
    case 'rewrite_applied':
      if (m.originalScore != null && m.currentScore != null) {
        return `AI rewrite applied to ${title}. Score improved ${m.originalScore} → ${m.currentScore}.`;
      }
      return `AI rewrite applied to ${title}.`;
  }
}

function formatCollapsed(kind: ActivityItem['kind'], events: ActivityItem[]): string {
  const count = events.length;
  const scores = events
    .map((e) => ({
      from: e.metadata?.originalScore,
      to: e.metadata?.currentScore,
    }))
    .filter((s): s is { from: number; to: number } => s.from != null && s.to != null);
  const avgFrom = scores.length > 0
    ? Math.round(scores.reduce((a, s) => a + s.from, 0) / scores.length)
    : null;
  const avgTo = scores.length > 0
    ? Math.round(scores.reduce((a, s) => a + s.to, 0) / scores.length)
    : null;

  switch (kind) {
    case 'disclosure_applied':
      return avgFrom != null
        ? `Disclosure label applied to ${count} products. Avg score improved ${avgFrom} → ${avgTo}.`
        : `Disclosure label applied to ${count} products.`;
    case 'disclosure_undone':
      return `Disclosure undone on ${count} products.`;
    case 'rewrite_applied':
      return avgFrom != null
        ? `AI rewrite applied to ${count} products. Avg score improved ${avgFrom} → ${avgTo}.`
        : `AI rewrite applied to ${count} products.`;
    case 'scan_webhook': {
      let critical = 0;
      let warning = 0;
      let clean = 0;
      for (const e of events) {
        if (!e.metadata?.issueCount) clean++;
        else if (e.metadata.severity === 'critical') critical++;
        else warning++;
      }
      const parts: string[] = [];
      if (clean > 0) parts.push(`${clean} clean`);
      if (warning > 0) parts.push(`${warning} warning`);
      if (critical > 0) parts.push(`${critical} critical`);
      return `${count} products auto-scanned (${parts.join(', ')}).`;
    }
    default:
      return `${count} events.`;
  }
}

function FilterButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '4px 12px', borderRadius: 16, fontSize: 12, fontWeight: 600,
        cursor: 'pointer', fontFamily: 'inherit',
        border: `1px solid ${active ? 'var(--p-color-border-hover)' : 'var(--p-color-border)'}`,
        background: active ? 'var(--p-color-bg-surface-secondary)' : 'var(--p-color-bg-surface)',
        color: active ? 'var(--p-color-text)' : 'var(--p-color-text-subdued)',
      }}
    >
      {label}
    </button>
  );
}

function filterLabel(filter: string, scan: ScanResult, t: (k: string) => string): string {
  if (filter === 'all') return t('results.filterAll');
  if (filter === 'critical') return `${t('severity.critical')} (${scan.summary.critical})`;
  if (filter === 'warning') return `${t('severity.warning')} (${scan.summary.warning})`;
  if (filter === 'info') return `${t('severity.info')} (${scan.summary.info})`;
  return filter;
}

function filterLabelEx(filter: string, scan: ScanResult, passedCount: number, fixedCount: number, t: (k: string) => string): string {
  if (filter === 'passed') return `Passed (${passedCount})`;
  if (filter === 'fixed') return `Fixed (${fixedCount})`;
  return filterLabel(filter, scan, t);
}

function RegulationBreakdown({ regulations }: { regulations: Record<string, number> }): JSX.Element | null {
  const { t } = useTranslation();
  const entries = Object.entries(regulations).filter(([, c]) => c > 0).sort(([, a], [, b]) => b - a);
  if (entries.length === 0) return null;
  const max = Math.max(...entries.map(([, c]) => c));

  const REG_COLORS: Record<string, string> = {
    EU_AI_ACT_50_2: '#D72C0D',
    EU_AI_ACT_50_IMG: '#D72C0D',
    FTC_AI_DISCLOSURE: '#B98900',
    NY_SYNTHETIC_PERFORMER: '#005BD3',
    BEST_PRACTICE_BORDERLINE: '#C9CCCF',
  };

  return (
    <Card>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>{t('dashboard.issuesByRegulation')}</div>
      {entries.map(([id, count]) => {
        const pct = max > 0 ? (count / max) * 100 : 0;
        const color = REG_COLORS[id] ?? '#C9CCCF';
        return (
          <div key={id} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
              <span>{regulationLabel(id, t)}</span>
              <strong style={{ fontWeight: 600 }}>{count} issues</strong>
            </div>
            <div style={{ height: 5, background: '#F6F6F7', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', borderRadius: 3, background: color, transition: 'width 0.3s' }} />
            </div>
          </div>
        );
      })}
    </Card>
  );
}

function MonitoringItem({ label, value, sub, valueColor }: { label: string; value: string; sub: string; valueColor?: string }): JSX.Element {
  return (
    <div style={{ background: '#F6F6F7', borderRadius: 8, padding: 14 }}>
      <div style={{ fontSize: 12, color: '#6D7175', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: valueColor }}>{value}</div>
      <div style={{ fontSize: 11, color: '#8C8C8C', marginTop: 2 }}>{sub}</div>
    </div>
  );
}

function StatsCell({ value, label, valueColor, subdued }: { value: string; label: string; valueColor?: string; subdued?: boolean }): JSX.Element {
  return (
    <div style={{ background: 'var(--p-color-bg-surface-secondary)', padding: 14, textAlign: 'center' }}>
      <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 2, color: valueColor ?? (subdued ? 'var(--p-color-text-subdued)' : undefined) }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--p-color-text-subdued)' }}>{label}</div>
    </div>
  );
}

function RegionRow({ flag, flagBg, name, source, tag, tone }: {
  flag: string; flagBg: string; name: string; source: string; tag: string;
  tone: 'critical' | 'warning' | 'info';
}): JSX.Element {
  return (
    <InlineStack align="space-between" blockAlign="center">
      <InlineStack gap="200" blockAlign="center">
        <div style={{
          width: 24, height: 16, borderRadius: 2, background: flagBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 9, fontWeight: 700, color: '#fff', flexShrink: 0,
        }}>
          {flag}
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{name}</div>
          <div style={{ fontSize: 11, color: 'var(--p-color-text-subdued)' }}>{source}</div>
        </div>
      </InlineStack>
      <Badge tone={tone}>{tag}</Badge>
    </InlineStack>
  );
}

function RegulationInfo({ color, title, desc, date, dateColor }: {
  color: string; title: string; desc: string; date: string; dateColor: string;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', borderBottom: '1px solid #F3F4F6' }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, marginTop: 5, flexShrink: 0 }} />
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{title}</div>
        <div style={{ fontSize: 12, color: 'var(--p-color-text-subdued)', lineHeight: 1.5 }}>{desc}</div>
        <div style={{ fontSize: 11, fontWeight: 600, color: dateColor, marginTop: 3 }}>{date}</div>
      </div>
    </div>
  );
}
